/**
 * Veltrona — collection and search results: AJAX filtering, sorting, view modes,
 * load more / infinite scroll and the mobile filter drawer.
 */
(() => {
  'use strict';

  const V = window.Veltrona;
  const cache = new Map();

  class VtFacets extends HTMLElement {
    connectedCallback() {
      // Mark the first entry too, so Back returns to the unfiltered results.
      if (!history.state?.facets) history.replaceState({ ...(history.state || {}), facets: true }, '');
      this.onPopState = (e) => {
        if (e.state?.facets && this.isConnected) this.load(window.location.search.slice(1), false);
      };
      window.addEventListener('popstate', this.onPopState);
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.sectionId = this.dataset.section;
      this.pagination = this.dataset.pagination;
      this.panel = this.querySelector('[data-facets-panel]');

      this.onFormChange = V.utils.debounce(() => this.submit(), 450);
      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-facet-form]') || e.target.matches('[data-sort]')) {
          if (e.target.matches('[data-range-min], [data-range-max]')) return;
          this.onFormChange();
        }
      });
      this.addEventListener('input', (e) => {
        if (e.target.matches('[data-input-min], [data-input-max]')) this.onFormChange();
      });

      this.addEventListener('click', (e) => {
        const remove = e.target.closest('[data-facet-remove], [data-facets-clear]');
        if (remove) {
          e.preventDefault();
          this.load(new URL(remove.href, window.location.origin).searchParams.toString());
          return;
        }
        if (e.target.closest('[data-facets-open]')) this.openPanel();
        if (e.target.closest('[data-facets-close]')) this.closePanel();
        const more = e.target.closest('[data-show-more]');
        if (more) this.toggleMore(more);
        const view = e.target.closest('[data-view]');
        if (view) this.setView(view.dataset.view);
        const cols = e.target.closest('[data-cols]');
        if (cols) this.setCols(cols.dataset.cols);
        const loadMore = e.target.closest('[data-load-more]');
        if (loadMore) {
          e.preventDefault();
          this.loadMore(loadMore);
        }
      });

      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.classList.contains('is-panel-open')) this.closePanel();
      });


      this.restorePrefs();
      this.watchInfinite();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    /* ---- Preferences ------------------------------------------------------ */
    restorePrefs() {
      try {
        const view = localStorage.getItem('vt-view');
        const cols = localStorage.getItem('vt-cols');
        if (view) this.setView(view, false);
        if (cols) this.setCols(cols, false);
      } catch (e) {
        /* storage unavailable */
      }
    }

    setView(view, save = true) {
      const grid = this.querySelector('[data-product-grid]');
      grid?.classList.toggle('collection-grid--list', view === 'list');
      this.querySelectorAll('[data-view]').forEach((b) => {
        const active = b.dataset.view === view;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active);
      });
      this.view = view;
      if (save) {
        try {
          localStorage.setItem('vt-view', view);
        } catch (e) {
          /* storage unavailable */
        }
      }
    }

    setCols(cols, save = true) {
      const grid = this.querySelector('[data-product-grid]');
      if (!grid || !this.querySelector(`[data-cols="${cols}"]`)) return;
      grid.style.setProperty('--cols', cols);
      this.querySelectorAll('[data-cols]').forEach((b) => {
        const active = b.dataset.cols === String(cols);
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active);
      });
      this.cols = cols;
      if (save) {
        try {
          localStorage.setItem('vt-cols', cols);
        } catch (e) {
          /* storage unavailable */
        }
      }
    }

    /* ---- Panel (mobile drawer / desktop drawer) --------------------------- */
    openPanel() {
      if (this.classList.contains('is-panel-open')) return;
      this.lastFocus = document.activeElement;
      this.classList.add('is-panel-open');
      this.querySelector('[data-facets-open]')?.setAttribute('aria-expanded', 'true');
      V.utils.lockScroll(true);
      setTimeout(() => V.utils.focusable(this.panel || this)[0]?.focus({ preventScroll: true }), 50);
    }

    closePanel() {
      if (!this.classList.contains('is-panel-open')) return;
      this.classList.remove('is-panel-open');
      this.querySelector('[data-facets-open]')?.setAttribute('aria-expanded', 'false');
      V.utils.lockScroll(false);
      this.lastFocus?.focus?.({ preventScroll: true });
    }

    toggleMore(button) {
      const list = button.previousElementSibling;
      const expanded = button.getAttribute('aria-expanded') === 'true';
      list?.querySelectorAll('.facet__more').forEach((li) => (li.hidden = expanded));
      button.setAttribute('aria-expanded', !expanded);
      button.querySelector('[data-more-label]').hidden = !expanded;
      button.querySelector('[data-less-label]').hidden = expanded;
    }

    /* ---- Loading results -------------------------------------------------- */
    formParams() {
      const form = this.querySelector('[data-facet-form]');
      const data = form ? new FormData(form) : new FormData();
      const sort = this.querySelector('[data-sort]');
      if (sort && !data.has('sort_by')) data.set('sort_by', sort.value);
      const params = new URLSearchParams();
      for (const [key, value] of data.entries()) {
        if (value === '') continue;
        params.append(key, value);
      }
      return params.toString();
    }

    submit() {
      this.load(this.formParams());
    }

    async load(params, push = true) {
      const url = `${window.location.pathname}?${params}`;
      this.classList.add('is-loading');
      try {
        const html = await this.fetchSection(params);
        this.render(html);
        if (push) window.history.pushState({ facets: true }, '', url);
      } catch (e) {
        window.location.href = url;
      } finally {
        this.classList.remove('is-loading');
      }
    }

    async fetchSection(params) {
      const key = `${window.location.pathname}?${params}`;
      if (cache.has(key)) return cache.get(key);
      const res = await fetch(`${window.location.pathname}?${params}${params ? '&' : ''}section_id=${this.sectionId}`);
      if (!res.ok) throw new Error(res.status);
      const text = await res.text();
      cache.set(key, text);
      return text;
    }

    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(`#CollectionLayout-${this.sectionId}, #SearchLayout-${this.sectionId}`);
      if (!fresh) return;

      // Keep which filter groups were collapsed and the drawer scroll position.
      const closed = new Set(
        Array.from(this.querySelectorAll('details.facet:not([open])')).map((d) => d.dataset.facetId)
      );
      const body = this.querySelector('[data-facets-body]');
      const scrollTop = body?.scrollTop || 0;
      const focusedId = document.activeElement?.id;

      ['[data-results]', '[data-active-wrap]', '[data-results-count]', '[data-facets-body]', '.facets__apply'].forEach((sel) => {
        const target = this.querySelector(sel);
        const source = fresh.querySelector(sel);
        if (target && source) target.innerHTML = source.innerHTML;
      });

      this.querySelectorAll('details.facet').forEach((d) => {
        if (closed.has(d.dataset.facetId)) d.open = false;
      });
      const newBody = this.querySelector('[data-facets-body]');
      if (newBody) newBody.scrollTop = scrollTop;
      if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });

      if (this.view) this.setView(this.view, false);
      if (this.cols) this.setCols(this.cols, false);
      V.Reveal.init(this);
      this.querySelectorAll('[data-results] [data-reveal]').forEach((el) => el.classList.add('is-revealed'));
      this.watchInfinite();

      const live = document.getElementById('LiveRegion');
      const count = this.querySelector('[data-results-count]');
      if (live && count) live.textContent = count.textContent.trim();

      if (!this.classList.contains('is-panel-open')) {
        const top = this.getBoundingClientRect().top + window.scrollY - (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-offset')) || 0) - 16;
        if (window.scrollY > top) window.scrollTo({ top, behavior: V.utils.reducedMotion() ? 'auto' : 'smooth' });
      }
    }

    async loadMore(button) {
      if (button.classList.contains('is-loading')) return;
      button.classList.add('is-loading');
      const next = new URL(button.href, window.location.origin);
      try {
        const res = await fetch(`${next.pathname}${next.search}${next.search ? '&' : '?'}section_id=${this.sectionId}`);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const grid = this.querySelector('[data-product-grid]');
        const freshGrid = doc.querySelector('[data-product-grid]');
        const items = freshGrid ? Array.from(freshGrid.children).filter((li) => !li.classList.contains('collection-grid__promo')) : [];
        items.forEach((li, i) => {
          li.classList.add('is-appended');
          li.style.setProperty('--i', i);
          li.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
          li.classList.add('is-revealed');
          grid.appendChild(li);
        });
        const wrap = this.querySelector('[data-load-more-wrap]');
        const freshWrap = doc.querySelector('[data-load-more-wrap]');
        if (wrap && freshWrap) wrap.innerHTML = freshWrap.innerHTML;
        this.watchInfinite();
        const live = document.getElementById('LiveRegion');
        if (live) live.textContent = wrap?.querySelector('.load-more__status')?.textContent.trim() || '';
        items[0]?.querySelector('a')?.focus({ preventScroll: true });
      } catch (e) {
        window.location.href = button.href;
      }
    }

    watchInfinite() {
      this.io?.disconnect();
      if (this.pagination !== 'infinite') return;
      const button = this.querySelector('[data-load-more]');
      if (!button) return;
      this.io = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) this.loadMore(button);
        },
        { rootMargin: '600px 0px' }
      );
      this.io.observe(button);
    }
  }

  /** Dual-handle price slider synced with the number inputs. */
  class VtPriceRange extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.max = Number(this.dataset.max) || 100;
      this.rMin = this.querySelector('[data-range-min]');
      this.rMax = this.querySelector('[data-range-max]');
      this.iMin = this.querySelector('[data-input-min]');
      this.iMax = this.querySelector('[data-input-max]');
      const commit = V.utils.debounce(() => this.iMin.dispatchEvent(new Event('input', { bubbles: true })), 350);

      const fromRange = (changed) => {
        let a = Number(this.rMin.value);
        let b = Number(this.rMax.value);
        if (a > b) {
          if (changed === 'min') a = b;
          else b = a;
          this.rMin.value = a;
          this.rMax.value = b;
        }
        this.iMin.value = a > 0 ? a : '';
        this.iMax.value = b < this.max ? b : '';
        this.paint();
        commit();
      };
      this.rMin.addEventListener('input', () => fromRange('min'));
      this.rMax.addEventListener('input', () => fromRange('max'));
      [this.iMin, this.iMax].forEach((input) =>
        input.addEventListener('input', () => {
          this.rMin.value = this.iMin.value || 0;
          this.rMax.value = this.iMax.value || this.max;
          this.paint();
        })
      );
      this.paint();
    }

    paint() {
      const a = (Number(this.rMin.value) / this.max) * 100;
      const b = (Number(this.rMax.value) / this.max) * 100;
      this.style.setProperty('--a', `${a}%`);
      this.style.setProperty('--b', `${b}%`);
    }
  }

  if (!customElements.get('vt-facets')) customElements.define('vt-facets', VtFacets);
  if (!customElements.get('vt-price-range')) customElements.define('vt-price-range', VtPriceRange);
})();
